"use client";

import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Dark/light toggle. The icon swap is an emerge/dissolve cross-fade (scale +
 * rotate) rather than an instant flip, so switching themes feels physical.
 * The page-wide colour cross-fade is handled by the CSS token transition on
 * <body> (see globals.css).
 */
export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Avoid a hydration mismatch — theme is only known on the client.
  useEffect(() => setMounted(true), []);

  const isDark = resolvedTheme === "dark";

  return (
    <motion.button
      type="button"
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      onClick={() => setTheme(isDark ? "light" : "dark")}
      whileTap={{ scale: 0.9 }}
      transition={{ type: "spring", stiffness: 500, damping: 24 }}
      className="neo relative grid h-11 w-11 place-items-center rounded-full glass text-accent"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={mounted ? (isDark ? "moon" : "sun") : "placeholder"}
          initial={{ scale: 0.4, rotate: -90, opacity: 0, filter: "blur(4px)" }}
          animate={{ scale: 1, rotate: 0, opacity: 1, filter: "blur(0px)" }}
          exit={{ scale: 0.4, rotate: 90, opacity: 0, filter: "blur(4px)" }}
          transition={{ type: "spring", stiffness: 320, damping: 22 }}
          className="text-lg leading-none"
        >
          {mounted ? (isDark ? "◐" : "◑") : "◐"}
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
}
